// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const TEMPLATES = [
  {
    id: 'business-landing',
    name: 'Business Landing Page',
    description: 'Clean hero section, features grid, testimonials carousel, and strong CTA. Perfect for startups and small businesses.',
    gradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    icon: '\uD83C\uDFE2',
  },
  {
    id: 'saas-product',
    name: 'SaaS Product Page',
    description: 'Dark theme with pricing tiers, feature comparison, demo request form, and integration showcase.',
    gradient: 'linear-gradient(135deg, #1e1b4b, #6366f1)',
    icon: '\uD83D\uDE80',
  },
  {
    id: 'ecommerce-store',
    name: 'E-Commerce Store',
    description: 'Product grid with filters, shopping cart, checkout flow, and inventory management dashboard.',
    gradient: 'linear-gradient(135deg, #10b981, #059669)',
    icon: '\uD83D\uDED2',
  },
  {
    id: 'restaurant-food',
    name: 'Restaurant / Food',
    description: 'Interactive menu display, photo gallery, online reservations, and embedded map for directions.',
    gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
    icon: '\uD83C\uDF7D\uFE0F',
  },
  {
    id: 'portfolio-creative',
    name: 'Portfolio / Creative',
    description: 'Masonry image gallery, project showcases, about section, and contact form for freelancers and agencies.',
    gradient: 'linear-gradient(135deg, #ec4899, #be185d)',
    icon: '\uD83C\uDFA8',
  },
  {
    id: 'event-conference',
    name: 'Event / Conference',
    description: 'Event schedule timeline, speaker bios, ticket purchasing, and sponsor logo grid.',
    gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)',
    icon: '\uD83C\uDF9F\uFE0F',
  },
  {
    id: 'real-estate',
    name: 'Real Estate Listing',
    description: 'Property grid with search filters, interactive map, virtual tour embeds, and agent contact forms.',
    gradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    icon: '\uD83C\uDFE0',
  },
  {
    id: 'professional-services',
    name: 'Professional Services',
    description: 'About the firm, services offered, team member profiles, and client testimonials with trust badges.',
    gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    icon: '\uD83D\uDCBC',
  },
];

const COLOR_PRESETS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b',
  '#10b981', '#14b8a6', '#3b82f6', '#1e293b', '#f97316',
];

const FONT_OPTIONS = [
  'Inter', 'Poppins', 'Montserrat', 'Roboto', 'Open Sans', 'Playfair Display',
];

const DEFAULT_PAGES = [
  { id: 'home', label: 'Home', enabled: true, required: true },
  { id: 'about', label: 'About', enabled: true, required: false },
  { id: 'services', label: 'Services', enabled: true, required: false },
  { id: 'portfolio', label: 'Portfolio', enabled: false, required: false },
  { id: 'blog', label: 'Blog', enabled: false, required: false },
  { id: 'contact', label: 'Contact', enabled: true, required: false },
  { id: 'pricing', label: 'Pricing', enabled: false, required: false },
  { id: 'faq', label: 'FAQ', enabled: false, required: false },
  { id: 'team', label: 'Team', enabled: false, required: false },
];

const HOSTING_TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0/mo',
    description: 'Cloudflare Pages — fast, secure, global CDN',
    features: ['Unlimited bandwidth', 'SSL included', 'Global CDN', 'pages.dev subdomain'],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$19/mo',
    description: 'Analytics, forms, and priority support',
    features: ['Everything in Free', 'Web analytics dashboard', 'Contact form submissions', 'Priority support', 'Custom domain included'],
  },
  {
    id: 'business',
    name: 'Business',
    price: '$49/mo',
    description: 'E-commerce, CMS, and advanced features',
    features: ['Everything in Pro', 'E-commerce integration', 'Content management system', 'Blog engine', 'Unlimited pages', 'A/B testing'],
  },
];

const PRICING_TIERS = [
  {
    name: 'Starter',
    oneTime: '$2,500',
    monthly: 'Free hosting',
    description: 'Template-based, 3 pages',
    features: [
      'Choose from 8 professional templates',
      'Up to 3 pages',
      'Custom branding & colors',
      'Mobile responsive',
      'Cloudflare Pages hosting',
      'SSL certificate included',
      '1 round of revisions',
      '48-hour delivery',
    ],
    highlighted: false,
  },
  {
    name: 'Professional',
    oneTime: '$5,000',
    monthly: '$19/mo',
    description: 'Custom design, 8 pages, forms, analytics',
    features: [
      'Fully custom design',
      'Up to 8 pages',
      'Contact forms with email notifications',
      'Web analytics dashboard',
      'SEO optimization',
      'Google Maps integration',
      'Social media integration',
      '3 rounds of revisions',
      'Priority support',
      '1-week delivery',
    ],
    highlighted: true,
  },
  {
    name: 'Enterprise',
    oneTime: '$10,000',
    monthly: '$49/mo',
    description: 'Full custom, unlimited pages, e-commerce, CMS, blog',
    features: [
      'Everything in Professional',
      'Unlimited pages',
      'E-commerce with cart & checkout',
      'Content management system',
      'Blog with categories & tags',
      'User authentication',
      'Admin dashboard',
      'API integrations',
      'A/B testing',
      'Unlimited revisions',
      'Dedicated project manager',
      '2-week delivery',
    ],
    highlighted: false,
  },
];

const DEMO_SITES = [
  {
    id: 1,
    name: 'Canstorr Commodities',
    domain: 'canstorr.pages.dev',
    template: 'Business Landing Page',
    status: 'Live',
    lastUpdated: 'Feb 10, 2026',
    views: 1284,
  },
  {
    id: 2,
    name: 'Marshall Auto Group',
    domain: 'marshallauto.pages.dev',
    template: 'Professional Services',
    status: 'Building',
    lastUpdated: 'Feb 11, 2026',
    views: 0,
  },
  {
    id: 3,
    name: 'Prairie Grain Co-op',
    domain: 'prairiegrain.pages.dev',
    template: 'E-Commerce Store',
    status: 'Updating',
    lastUpdated: 'Feb 9, 2026',
    views: 873,
  },
];

const STEP_LABELS = ['Template', 'Branding', 'Content', 'Pages', 'Domain & Hosting', 'Review & Launch'];

function WebsiteBuilderPage() {
  const { user } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const [branding, setBranding] = useState({
    businessName: '',
    tagline: '',
    primaryColor: '#6366f1',
    secondaryColor: '#10b981',
    logoFile: null,
    font: 'Inter',
  });

  const [content, setContent] = useState({
    heroHeadline: '',
    heroSubheading: '',
    aboutText: '',
    features: [{ title: '', description: '', icon: '\uD83D\uDD27' }],
    contactEmail: '',
    contactPhone: '',
    address: '',
  });

  const [pages, setPages] = useState(DEFAULT_PAGES.map(p => ({ ...p })));
  const [subdomain, setSubdomain] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [hostingTier, setHostingTier] = useState('free');
  const [launched, setLaunched] = useState(false);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setCurrentStep(1);
  };

  const handleBrandingChange = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };

  const handleContentChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleFeatureChange = (index, field, value) => {
    setContent(prev => {
      const updated = [...prev.features];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, features: updated };
    });
  };

  const addFeature = () => {
    if (content.features.length < 6) {
      setContent(prev => ({
        ...prev,
        features: [...prev.features, { title: '', description: '', icon: '\u2B50' }],
      }));
    }
  };

  const removeFeature = (index) => {
    setContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const togglePage = (pageId) => {
    setPages(prev => prev.map(p =>
      p.id === pageId && !p.required ? { ...p, enabled: !p.enabled } : p
    ));
  };

  const handleLaunch = () => {
    setLaunched(true);
  };

  const resetBuilder = () => {
    setSelectedTemplate(null);
    setCurrentStep(0);
    setBranding({ businessName: '', tagline: '', primaryColor: '#6366f1', secondaryColor: '#10b981', logoFile: null, font: 'Inter' });
    setContent({ heroHeadline: '', heroSubheading: '', aboutText: '', features: [{ title: '', description: '', icon: '\uD83D\uDD27' }], contactEmail: '', contactPhone: '', address: '' });
    setPages(DEFAULT_PAGES.map(p => ({ ...p })));
    setSubdomain('');
    setCustomDomain('');
    setHostingTier('free');
    setLaunched(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Live': return '#10b981';
      case 'Building': return '#f59e0b';
      case 'Updating': return '#3b82f6';
      default: return 'var(--text-muted)';
    }
  };

  const renderTemplateGallery = () => (
    <div style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Choose a Template</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
        Select a starting point for your website. Every template is fully customizable.
      </p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 16,
      }}>
        {TEMPLATES.map(template => {
          const isSelected = selectedTemplate?.id === template.id;
          return (
            <div
              key={template.id}
              style={{
                background: 'var(--card)',
                border: isSelected ? '2px solid #6366f1' : '1px solid var(--border)',
                borderRadius: 12,
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'border-color 0.2s, transform 0.2s',
                transform: isSelected ? 'scale(1.02)' : 'scale(1)',
              }}
              onClick={() => handleSelectTemplate(template)}
            >
              <div style={{
                height: 140,
                background: template.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 48,
              }}>
                {template.icon}
              </div>
              <div style={{ padding: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                  {template.name}
                </h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 12 }}>
                  {template.description}
                </p>
                <button
                  style={{
                    width: '100%',
                    padding: '8px 16px',
                    background: isSelected ? '#6366f1' : 'transparent',
                    color: isSelected ? '#fff' : '#6366f1',
                    border: '1px solid #6366f1',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                  onClick={(e) => { e.stopPropagation(); handleSelectTemplate(template); }}
                >
                  {isSelected ? '\u2713 Selected' : 'Select'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderStepIndicator = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      marginBottom: 28,
      overflowX: 'auto',
      paddingBottom: 4,
    }}>
      {STEP_LABELS.map((label, index) => (
        <React.Fragment key={label}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: index <= currentStep ? 'pointer' : 'default',
              opacity: index <= currentStep ? 1 : 0.4,
              flexShrink: 0,
            }}
            onClick={() => {
              if (index === 0 || (index <= currentStep)) {
                setCurrentStep(index);
              }
            }}
          >
            <div style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: index < currentStep ? '#10b981' : index === currentStep ? '#6366f1' : 'var(--border)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 600,
              flexShrink: 0,
            }}>
              {index < currentStep ? '\u2713' : index + 1}
            </div>
            <span style={{
              fontSize: 13,
              fontWeight: index === currentStep ? 600 : 400,
              color: index === currentStep ? 'var(--text)' : 'var(--text-muted)',
              whiteSpace: 'nowrap',
            }}>
              {label}
            </span>
          </div>
          {index < STEP_LABELS.length - 1 && (
            <div style={{
              width: 24,
              height: 1,
              background: index < currentStep ? '#10b981' : 'var(--border)',
              flexShrink: 0,
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderBrandingStep = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 20 }}>Branding</h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>Business Name *</label>
          <input
            type="text"
            value={branding.businessName}
            onChange={(e) => handleBrandingChange('businessName', e.target.value)}
            placeholder="Your Business Name"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Tagline</label>
          <input
            type="text"
            value={branding.tagline}
            onChange={(e) => handleBrandingChange('tagline', e.target.value)}
            placeholder="Your catchy tagline"
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>Primary Color</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {COLOR_PRESETS.map(color => (
              <div
                key={color}
                onClick={() => handleBrandingChange('primaryColor', color)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: color,
                  cursor: 'pointer',
                  border: branding.primaryColor === color ? '3px solid #fff' : '2px solid transparent',
                  boxShadow: branding.primaryColor === color ? '0 0 0 2px #6366f1' : 'none',
                }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="color"
              value={branding.primaryColor}
              onChange={(e) => handleBrandingChange('primaryColor', e.target.value)}
              style={{ width: 40, height: 32, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }}
            />
            <input
              type="text"
              value={branding.primaryColor}
              onChange={(e) => handleBrandingChange('primaryColor', e.target.value)}
              style={{ ...inputStyle, width: 120 }}
              maxLength={7}
            />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Secondary Color</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {COLOR_PRESETS.map(color => (
              <div
                key={color}
                onClick={() => handleBrandingChange('secondaryColor', color)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: color,
                  cursor: 'pointer',
                  border: branding.secondaryColor === color ? '3px solid #fff' : '2px solid transparent',
                  boxShadow: branding.secondaryColor === color ? '0 0 0 2px #6366f1' : 'none',
                }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="color"
              value={branding.secondaryColor}
              onChange={(e) => handleBrandingChange('secondaryColor', e.target.value)}
              style={{ width: 40, height: 32, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }}
            />
            <input
              type="text"
              value={branding.secondaryColor}
              onChange={(e) => handleBrandingChange('secondaryColor', e.target.value)}
              style={{ ...inputStyle, width: 120 }}
              maxLength={7}
            />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>Logo Upload</label>
          <div style={{
            border: '2px dashed var(--border)',
            borderRadius: 12,
            padding: 24,
            textAlign: 'center',
            cursor: 'pointer',
            background: 'var(--bg)',
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>{'\uD83D\uDDBC\uFE0F'}</div>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 4 }}>
              Drag & drop your logo here
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              PNG, SVG, or JPG (max 2MB)
            </p>
          </div>
        </div>
        <div>
          <label style={labelStyle}>Font Family</label>
          <select
            value={branding.font}
            onChange={(e) => handleBrandingChange('font', e.target.value)}
            style={inputStyle}
          >
            {FONT_OPTIONS.map(font => (
              <option key={font} value={font}>{font}</option>
            ))}
          </select>
          <div style={{
            marginTop: 12,
            padding: 16,
            background: 'var(--bg)',
            borderRadius: 8,
            border: '1px solid var(--border)',
          }}>
            <p style={{ fontFamily: branding.font, fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
              The quick brown fox
            </p>
            <p style={{ fontFamily: branding.font, fontSize: 14, color: 'var(--text-muted)' }}>
              jumps over the lazy dog
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContentStep = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 20 }}>Content</h3>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Hero Headline *</label>
        <input
          type="text"
          value={content.heroHeadline}
          onChange={(e) => handleContentChange('heroHeadline', e.target.value)}
          placeholder="Your powerful headline that grabs attention"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Hero Subheading</label>
        <textarea
          value={content.heroSubheading}
          onChange={(e) => handleContentChange('heroSubheading', e.target.value)}
          placeholder="A brief description that supports your headline..."
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>About Section</label>
        <textarea
          value={content.aboutText}
          onChange={(e) => handleContentChange('aboutText', e.target.value)}
          placeholder="Tell visitors about your business, mission, and values..."
          rows={4}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>
          Services / Features ({content.features.length}/6)
        </label>
        {content.features.map((feature, index) => (
          <div key={index} style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 2fr auto',
            gap: 8,
            marginBottom: 8,
            alignItems: 'start',
          }}>
            <input
              type="text"
              value={feature.icon}
              onChange={(e) => handleFeatureChange(index, 'icon', e.target.value)}
              placeholder="\uD83D\uDD27"
              style={{ ...inputStyle, textAlign: 'center', fontSize: 20, padding: '8px 4px' }}
              maxLength={4}
            />
            <input
              type="text"
              value={feature.title}
              onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
              placeholder="Feature title"
              style={inputStyle}
            />
            <input
              type="text"
              value={feature.description}
              onChange={(e) => handleFeatureChange(index, 'description', e.target.value)}
              placeholder="Brief description of this feature or service"
              style={inputStyle}
            />
            {content.features.length > 1 && (
              <button
                onClick={() => removeFeature(index)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  color: '#ef4444',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  fontSize: 14,
                }}
              >
                {'\u2715'}
              </button>
            )}
          </div>
        ))}
        {content.features.length < 6 && (
          <button
            onClick={addFeature}
            style={{
              background: 'transparent',
              border: '1px dashed var(--border)',
              borderRadius: 8,
              color: '#6366f1',
              cursor: 'pointer',
              padding: '10px 16px',
              fontSize: 14,
              width: '100%',
              marginTop: 4,
            }}
          >
            + Add Feature
          </button>
        )}
      </div>

      <h4 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Contact Information</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        <div>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            value={content.contactEmail}
            onChange={(e) => handleContentChange('contactEmail', e.target.value)}
            placeholder="info@yourbusiness.com"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Phone</label>
          <input
            type="tel"
            value={content.contactPhone}
            onChange={(e) => handleContentChange('contactPhone', e.target.value)}
            placeholder="(555) 123-4567"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Address</label>
          <input
            type="text"
            value={content.address}
            onChange={(e) => handleContentChange('address', e.target.value)}
            placeholder="123 Main St, City, State"
            style={inputStyle}
          />
        </div>
      </div>
    </div>
  );

  const renderPagesStep = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Pages</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
        Select which pages to include in your website. Home is always included.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {pages.map(page => (
          <div
            key={page.id}
            onClick={() => togglePage(page.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '14px 16px',
              background: page.enabled ? 'rgba(99, 102, 241, 0.1)' : 'var(--card)',
              border: page.enabled ? '1px solid #6366f1' : '1px solid var(--border)',
              borderRadius: 10,
              cursor: page.required ? 'default' : 'pointer',
              opacity: page.required ? 0.8 : 1,
            }}
          >
            <div style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              border: page.enabled ? 'none' : '2px solid var(--border)',
              background: page.enabled ? '#6366f1' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              flexShrink: 0,
            }}>
              {page.enabled ? '\u2713' : ''}
            </div>
            <div>
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>
                {page.label}
              </span>
              {page.required && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>Required</span>
              )}
            </div>
          </div>
        ))}
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 16 }}>
        {pages.filter(p => p.enabled).length} page{pages.filter(p => p.enabled).length !== 1 ? 's' : ''} selected
      </p>
    </div>
  );

  const renderDomainStep = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 20 }}>Domain & Hosting</h3>

      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle}>Subdomain (Free)</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <input
            type="text"
            value={subdomain}
            onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="your-business"
            style={{
              ...inputStyle,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderRight: 'none',
            }}
          />
          <div style={{
            padding: '10px 16px',
            background: 'var(--bg)',
            border: '1px solid var(--border)',
            borderTopRightRadius: 8,
            borderBottomRightRadius: 8,
            color: 'var(--text-muted)',
            fontSize: 14,
            whiteSpace: 'nowrap',
          }}>
            .pages.dev
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 28 }}>
        <label style={labelStyle}>Custom Domain (Optional)</label>
        <input
          type="text"
          value={customDomain}
          onChange={(e) => setCustomDomain(e.target.value)}
          placeholder="yourdomain.com"
          style={inputStyle}
        />
        <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>
          We handle DNS configuration and SSL setup for your custom domain.
        </p>
      </div>

      <label style={labelStyle}>Hosting Tier</label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
        {HOSTING_TIERS.map(tier => {
          const isSelected = hostingTier === tier.id;
          return (
            <div
              key={tier.id}
              onClick={() => setHostingTier(tier.id)}
              style={{
                padding: 20,
                background: isSelected ? 'rgba(99, 102, 241, 0.08)' : 'var(--card)',
                border: isSelected ? '2px solid #6366f1' : '1px solid var(--border)',
                borderRadius: 12,
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{tier.name}</h4>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#6366f1' }}>{tier.price}</span>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>{tier.description}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {tier.features.map((feature, i) => (
                  <li key={i} style={{ fontSize: 13, color: 'var(--text-muted)', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#10b981', fontSize: 12 }}>{'\u2713'}</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderReviewStep = () => {
    const selectedPages = pages.filter(p => p.enabled).map(p => p.label);
    const selectedHosting = HOSTING_TIERS.find(t => t.id === hostingTier);
    const domainDisplay = customDomain || (subdomain ? `${subdomain}.pages.dev` : 'Not configured');

    return (
      <div>
        <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 20 }}>Review & Launch</h3>

        {launched ? (
          <div style={{
            textAlign: 'center',
            padding: '48px 24px',
            background: 'rgba(16, 185, 129, 0.08)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 16,
            marginBottom: 24,
          }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>{'\uD83D\uDE80'}</div>
            <h3 style={{ fontSize: 22, fontWeight: 700, color: '#10b981', marginBottom: 8 }}>
              Website Launch Initiated!
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 15, maxWidth: 500, margin: '0 auto 16px' }}>
              Our team will deploy your site within 24 hours. You will receive a confirmation email
              with your live URL and admin credentials.
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 24 }}>
              Domain: <strong style={{ color: 'var(--text)' }}>{domainDisplay}</strong>
            </p>
            <button
              onClick={resetBuilder}
              style={{
                padding: '10px 24px',
                background: 'transparent',
                color: '#6366f1',
                border: '1px solid #6366f1',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Build Another Website
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
              <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Template</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 10,
                    background: selectedTemplate?.gradient || 'var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                  }}>
                    {selectedTemplate?.icon}
                  </div>
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{selectedTemplate?.name}</p>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selectedTemplate?.id}</p>
                  </div>
                </div>
              </div>

              <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Branding</h4>
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                  {branding.businessName || 'Not set'}
                </p>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
                  {branding.tagline || 'No tagline'}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 20, height: 20, borderRadius: 4, background: branding.primaryColor }} />
                  <div style={{ width: 20, height: 20, borderRadius: 4, background: branding.secondaryColor }} />
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{branding.font}</span>
                </div>
              </div>

              <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Pages & Content</h4>
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                  {selectedPages.length} page{selectedPages.length !== 1 ? 's' : ''}
                </p>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
                  {selectedPages.join(', ')}
                </p>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {content.features.filter(f => f.title).length} feature{content.features.filter(f => f.title).length !== 1 ? 's' : ''} configured
                </p>
              </div>

              <div style={{ background: 'var(--bg)', borderRadius: 12, padding: 20, border: '1px solid var(--border)' }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>Domain & Hosting</h4>
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                  {domainDisplay}
                </p>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {selectedHosting?.name} tier — {selectedHosting?.price}
                </p>
              </div>
            </div>

            <div style={{
              background: 'var(--bg)',
              borderRadius: 16,
              border: '1px solid var(--border)',
              overflow: 'hidden',
              marginBottom: 24,
            }}>
              <div style={{
                background: 'var(--card)',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                borderBottom: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444' }} />
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#f59e0b' }} />
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#10b981' }} />
                </div>
                <div style={{
                  flex: 1,
                  background: 'var(--bg)',
                  borderRadius: 6,
                  padding: '4px 12px',
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  textAlign: 'center',
                }}>
                  {domainDisplay}
                </div>
              </div>
              <div style={{
                height: 300,
                background: `linear-gradient(135deg, ${branding.primaryColor}22, ${branding.secondaryColor}22)`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 32,
              }}>
                <div style={{
                  width: 56,
                  height: 56,
                  borderRadius: 12,
                  background: branding.primaryColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 24,
                  fontWeight: 700,
                  marginBottom: 16,
                }}>
                  {(branding.businessName || 'W').charAt(0).toUpperCase()}
                </div>
                <h2 style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: 'var(--text)',
                  marginBottom: 8,
                  textAlign: 'center',
                  fontFamily: branding.font,
                }}>
                  {content.heroHeadline || branding.businessName || 'Your Website'}
                </h2>
                <p style={{
                  fontSize: 16,
                  color: 'var(--text-muted)',
                  textAlign: 'center',
                  maxWidth: 500,
                  fontFamily: branding.font,
                }}>
                  {content.heroSubheading || branding.tagline || 'Your tagline will appear here'}
                </p>
                <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                  <div style={{
                    padding: '10px 24px',
                    background: branding.primaryColor,
                    color: '#fff',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 500,
                  }}>
                    Get Started
                  </div>
                  <div style={{
                    padding: '10px 24px',
                    background: 'transparent',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 500,
                  }}>
                    Learn More
                  </div>
                </div>
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleLaunch}
                style={{
                  padding: '14px 48px',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: 12,
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                }}
              >
                {'\uD83D\uDE80'} Launch Website
              </button>
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                Our team will deploy your site within 24 hours.
              </p>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderTemplateGallery();
      case 1: return renderBrandingStep();
      case 2: return renderContentStep();
      case 3: return renderPagesStep();
      case 4: return renderDomainStep();
      case 5: return renderReviewStep();
      default: return null;
    }
  };

  const renderStepNavigation = () => {
    if (currentStep === 0) return null;
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
        <button
          onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
          style={{
            padding: '10px 24px',
            background: 'transparent',
            color: 'var(--text-muted)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          {'\u2190'} Back
        </button>
        {currentStep < 5 && (
          <button
            onClick={() => setCurrentStep(prev => Math.min(5, prev + 1))}
            style={{
              padding: '10px 24px',
              background: '#6366f1',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Next {'\u2192'}
          </button>
        )}
      </div>
    );
  };

  const renderActiveSites = () => (
    <div style={{ marginTop: 40 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Active Websites</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>
        Manage your deployed websites.
      </p>
      <div style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 2fr 2fr 100px 120px 80px 140px',
          padding: '12px 20px',
          background: 'var(--bg)',
          borderBottom: '1px solid var(--border)',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}>
          <span>Site Name</span>
          <span>Domain</span>
          <span>Template</span>
          <span>Status</span>
          <span>Last Updated</span>
          <span>Views</span>
          <span>Actions</span>
        </div>
        {DEMO_SITES.map(site => (
          <div
            key={site.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 2fr 2fr 100px 120px 80px 140px',
              padding: '14px 20px',
              borderBottom: '1px solid var(--border)',
              alignItems: 'center',
              fontSize: 14,
            }}
          >
            <span style={{ fontWeight: 500, color: 'var(--text)' }}>{site.name}</span>
            <span style={{ color: '#6366f1', fontSize: 13 }}>{site.domain}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{site.template}</span>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: getStatusColor(site.status),
              fontWeight: 500,
            }}>
              <span style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: getStatusColor(site.status),
              }} />
              {site.status}
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{site.lastUpdated}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{site.views.toLocaleString()}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={{
                padding: '5px 12px',
                background: 'transparent',
                color: '#6366f1',
                border: '1px solid #6366f1',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
              }}>
                Visit
              </button>
              <button style={{
                padding: '5px 12px',
                background: 'transparent',
                color: 'var(--text-muted)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
              }}>
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPricing = () => (
    <div style={{ marginTop: 40 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Pricing</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
        Professional websites deployed on Cloudflare Pages with global CDN.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
        {PRICING_TIERS.map(tier => (
          <div
            key={tier.name}
            style={{
              background: tier.highlighted ? 'rgba(99, 102, 241, 0.06)' : 'var(--card)',
              border: tier.highlighted ? '2px solid #6366f1' : '1px solid var(--border)',
              borderRadius: 16,
              padding: 28,
              position: 'relative',
            }}
          >
            {tier.highlighted && (
              <div style={{
                position: 'absolute',
                top: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#6366f1',
                color: '#fff',
                padding: '4px 16px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
              }}>
                Most Popular
              </div>
            )}
            <h3 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
              {tier.name}
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
              {tier.description}
            </p>
            <div style={{ marginBottom: 20 }}>
              <span style={{ fontSize: 32, fontWeight: 700, color: '#6366f1' }}>{tier.oneTime}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 14, marginLeft: 4 }}>one-time</span>
              {tier.monthly !== 'Free hosting' && (
                <span style={{ color: 'var(--text-muted)', fontSize: 14, marginLeft: 8 }}>
                  + {tier.monthly}
                </span>
              )}
              {tier.monthly === 'Free hosting' && (
                <span style={{ color: '#10b981', fontSize: 14, marginLeft: 8, fontWeight: 500 }}>
                  + Free hosting
                </span>
              )}
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
              {tier.features.map((feature, i) => (
                <li key={i} style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  padding: '5px 0',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                }}>
                  <span style={{ color: '#10b981', fontSize: 13, marginTop: 2, flexShrink: 0 }}>{'\u2713'}</span>
                  {feature}
                </li>
              ))}
            </ul>
            <button
              onClick={() => {
                setCurrentStep(0);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: tier.highlighted ? '#6366f1' : 'transparent',
                color: tier.highlighted ? '#fff' : '#6366f1',
                border: tier.highlighted ? 'none' : '1px solid #6366f1',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Get Started
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
          {'\uD83C\uDF10'} Website Builder
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 15, maxWidth: 600 }}>
          Launch a professional website in hours. Deployed on Cloudflare Pages — fast, secure, global.
        </p>
      </div>

      {currentStep > 0 && selectedTemplate && (
        <div style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: 28,
          marginBottom: 28,
        }}>
          {renderStepIndicator()}
          {renderCurrentStep()}
          {renderStepNavigation()}
        </div>
      )}

      {currentStep === 0 && (
        <>
          {renderTemplateGallery()}
        </>
      )}

      {renderActiveSites()}
      {renderPricing()}
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-muted)',
  marginBottom: 6,
};

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  color: 'var(--text)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

export default WebsiteBuilderPage;
